import React, { useState, useEffect, useRef, useCallback } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../Config";
import { IoMdArrowBack } from "react-icons/io";
import Pagination from "../Component/Pagination";
import "./equipmentCountView.css";
import { MdAttachFile } from "react-icons/md";
import { IoCloseOutline } from "react-icons/io5";



// Debounce utility function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

function EquipmentCountView({ isSidebarExpanded }) {
  const { frequency_id } = useParams();
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [isAscending, setIsAscending] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [categoryName, setCategoryName] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isPopupUserOpen, setIsPopupUserOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);





  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "NA";
    return date.toLocaleDateString("en-GB");
  };

  const fetchData = async (currentPage, perPage, searchQuery = "") => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = {
        page: currentPage,
        per_page: perPage,
        q: searchQuery.trim(), // ✅ use correct query param
      };

      const response = await axios.get(
        `${API_URL}/api/frequency_configs/${frequency_id}/equipment`,
        {
          headers: { Authorization: authKey },
          params,
        }
      );

      setEquipments(response.data.Items || []);
      setTotalItems(response.data.TotalItems || 0);
      // Set category name from first item (if available)
      if (response.data.Items && response.data.Items.length > 0) {
        setCategoryName(response.data.Items[0].Category || "NA");
      } else {
        setCategoryName("NA");
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching equipment:", error.message);
      setLoading(false);
    }
  };


  const handleExport = async () => {
    if (!frequency_id) return;
    try {
      setIsExporting(true);
      const authKey = localStorage.getItem("authKey");

      const params = { max_rows: Math.max(1, totalItems || 1) };

      const res = await axios.get(
        `${API_URL}/api/export_equipment_by_frequency/${frequency_id}`,
        {
          headers: { Authorization: authKey },
          params,
          responseType: "blob",
          validateStatus: () => true,
        }
      );

      const contentType = res.headers?.["content-type"] || "";
      if (res.status !== 200 || contentType.includes("application/json")) {
        let message = "Export failed";
        try {
          const text = await res.data.text();
          const json = JSON.parse(text);
          if (json?.message) message = json.message;
        } catch (_) { }
        alert(message);
        return;
      }

      const headerName =
        res.headers?.["content-disposition"]?.match(/filename="?([^"]+)"?/)?.[1];
      const safeCategory = (categoryName || "equipment").replace(/\s+/g, "_");
      const fallbackName = `equipment_${safeCategory}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      const fileName = headerName || fallbackName;

      // Download
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error:", err);
      alert("Something went wrong while exporting.");
    } finally {
      setIsExporting(false);
    }
  };


  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      setPage(1);
      fetchData(1, itemsPerPage, searchQuery);
    }, 300),
    [itemsPerPage, frequency_id]
  );

  useEffect(() => {
    fetchData(page, itemsPerPage, searchInput);
  }, [page, itemsPerPage, frequency_id, searchInput]);

  const handlePaginationChange = (currentPage, newItemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(newItemsPerPage);

    if (!searchInput || searchInput.trim() === "") {
      localStorage.setItem("currentPage", currentPage);
      localStorage.setItem("itemsPerPage", newItemsPerPage);
    }
  };

  const containerRef = useRef(null);

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (value.trim() === "") {
      setPage(1);
      fetchData(1, itemsPerPage, "");
    } else {
      debouncedSearch(value);
    }
  };

  const handleSortpopup = () => setIsSortPopup(!isSortPopup);
  const closePopups = () => setIsSortPopup(false);

  const handleRefresh = () => {
    setSearchInput("");
    setPage(1);
    fetchData(1, itemsPerPage, "");
  };

  const handleBack = () => navigate("/frequency");

  const getStatusColor = (status) => {
    if (!status) return "black";
    switch (status.toLowerCase()) {
      case "active":
        return "#2980B9";
      case "cancelled":
        return "#F81700";
      case "hold":
        return "#D4AC0D";
      case "not in tenure":
        return "#313639";
      case "overdue":
        return "#C0612B";
      case "in progress":
        return "#df7f26";
      case "completed":
        return "#2E8B57";
      default:
        return "black";
    }
  };

  const handleIconClick = (index) => {
    // setIsPopupOpen(inquiryID);
    setIsPopupOpen(index === isPopupOpen ? null : index);
    setSelectedRowIndex(false);
    setIsPopupUserOpen(false);
  };

  const handleCloseAttachment = () => {
    setIsPopupOpen(false);
  };

  const handleUserCloseAttachment = () => {
    setIsPopupUserOpen(false);
  };

  const handleUserIconClick = (index) => {
    // setIsPopupOpen(inquiryID);
    setIsPopupUserOpen(index === isPopupOpen ? null : index);

    setSelectedRowIndex(false);
    setIsPopupOpen(false);
  };

  return (
    <div
      className={`data-list-content-container page-content ${isSidebarExpanded ? "expanded" : "collapsed"
        }`}
    >
      <div className="data-list-content" ref={containerRef}>
        <div className="data-list-header">
          <div className="data-list-header-content">
            <div className="data-list-heading-content-left-side display_flex_content">
              <Tooltip title="Back" arrow>
                <IoMdArrowBack className="back-company" onClick={handleBack} />
              </Tooltip>
              <span className="report-name">Equipment List</span>
              {/* <span className="report-name"
              style={{
                    color: "#21263c",
                    fontSize: "16px",
                    fontWeight: "500",
                  }}> Category :</span>

              {categoryName && (
                <span
                  style={{
                    backgroundColor: "white",
                    color: "#007bff",
                    fontSize: "14px",
                    fontWeight: "500",
                    padding: "4px 10px",
                    borderRadius: "12px",
                    marginLeft: "10px",
                    display: "inline-block",
                    border: "1px solid #007bff"
                  }}
                >
                  {categoryName}
                </span>
              )} */}

              <span className="report-name">
                {categoryName && (
                  <div className="category-badge-wrapper">
                    <span className="category-label">Category:</span>
                    <span
                      className="status-show"
                      style={{
                        backgroundColor: "white",
                        color: "#C0612B",
                        fontSize: "17px",
                        fontWeight: "500",
                        marginLeft: "10px",
                        display: "inline-block",
                      }}
                    >
                      {categoryName}
                    </span>
                  </div>
                )}
              </span>
            </div>

            <div className="data-list-heading-content-right-side">
              <div className="search-bar-container">
                <input
                  type="text"
                  placeholder="Search ..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  className="search-bar"
                />
                <IoSearchOutline className="search-bar-icon" />
              </div>

              <button className="equip-export-btn" onClick={handleExport} disabled={isExporting}>
                {isExporting ? "Exporting..." : "Export"}
              </button>


              <Tooltip title="Refresh List" arrow>
                <button className="refresh-button" onClick={handleRefresh}>
                  <TbRefresh className="refresh-icon" />
                </button>
              </Tooltip>
              <button className="sort-button" onClick={handleSortpopup}>
                <PiDotsThreeOutlineVerticalFill className="sort-icon" />
              </button>
              {isSortPopup && (
                <div className="sort-popup-container">
                  <div className="sort-popup-content">
                    <div className="sort-by">
                      SORT BY {isAscending ? "(Ascending)" : "(Descending)"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="data-list-body" onClick={closePopups}>
          <div className="data-list-scroll-body">
            <div className="data-list-table">
              {loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <div>Loading...</div>
                </div>
              ) : equipments.length > 0 ? (
                <table className="table multilevel-data">
                  <thead>
                    <tr>
                      <th className="">SR. NO.</th>
                      {/* <th>DEPARTMENT</th>
                      <th>SUB DEPARTMENT</th>
                      <th>EQUIPMENT TYPE</th>
                      <th>EQUIPMENT SUB TYPE</th> */}
                      <th className="center-alignment">CATEGORY</th>
                      <th className="center-alignment">TAG NO</th>
                      <th className="center-alignment">STATUS</th>
                      <th className="center-alignment">
                        LAST MAINTENANCE DATE
                      </th>
                      <th className="center-alignment">PR COPY ATTACHMENT </th>
                      <th className="center-alignment">
                        USER MANUAL ATTACHMENT{" "}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="data-list-scroll-bodys">
                    {equipments.map((item, index) => (
                      <tr key={item.ID} className="hoverable-row">
                        <td className="center-alignment">
                          {item.serial_no || index + 1}
                        </td>
                        {/* <td>{item.DepartmentName || "NA"}</td>
                        <td>{item.SubDepartmentName || "NA"}</td>
                        <td>{item.EquipmentType || "NA"}</td>
                        <td>{item.EquipmentSubType || "NA"}</td> */}
                        <td className="center-alignment">
                          {item.Category || "NA"}
                        </td>
                        <td className="center-alignment">
                          {item.TagNo || "NA"}
                        </td>
                        <td
                          className="status-schedules"
                          style={{ textAlign: "left" }}
                        >
                          <div
                            style={{
                              backgroundColor: getStatusColor(item.Status),
                              width: "6vw",
                            }}
                          >
                            {item.Status
                              ? item.Status.charAt(0).toUpperCase() +
                              item.Status.slice(1).toLowerCase()
                              : "NA"}
                          </div>
                        </td>
                        <td className="center-alignment">
                          {formatDate(item.LastMaintenanceDate)}
                        </td>
                        <td className="center-alignment">
                          <span
                            className="inquiry-file-btns"
                            onClick={() => handleIconClick(index)}
                            ref={buttonRef}
                          >
                            <MdAttachFile className="inquiry-file-icon" />
                          </span>
                          {isPopupOpen === index && (
                            <div
                              className="inquiry-attach-popup Pr-popup"
                              ref={popupRef}
                            >
                              <div className="attach-popup-contant">
                                <div className="attachment-header">
                                  <div className="attachment-header-content">
                                    PR Copy Attachments
                                  </div>
                                  <button
                                    className="fiile-close-btn"
                                    onClick={handleCloseAttachment}
                                  >
                                    <IoCloseOutline className="file-close-icon" />
                                  </button>
                                </div>
                                <div
                                  className="attach-popup-contant-body"
                                  key={index}
                                >
                                  <div className="inquiry-attach-file-containerssss">
                                    <div className="attach-file-showsss">
                                      <div className="text-attachmentss">
                                        {item.files &&
                                          item.files.filter(
                                            (file) => file.file_type === "pr_copy"
                                          ).length > 0 ? (
                                          <div className="attach-popup-content-body">
                                            {item.files
                                              .filter(
                                                (file) =>
                                                  file.file_type === "pr_copy"
                                              )
                                              .map(
                                                (
                                                  attachment,
                                                  attachmentIndex
                                                ) => (
                                                  <div
                                                    className="inquiry-attach-file-container"
                                                    key={attachmentIndex}
                                                  >
                                                    <div className="inquiry-attacth-file-model">
                                                      <div className="attach-file-show">
                                                        <div className="text-attachment">
                                                          {attachment.filename
                                                            .length > 20
                                                            ? `${attachment.filename.substring(
                                                              0,
                                                              15
                                                            )}...`
                                                            : attachment.filename}
                                                        </div>
                                                        <div className="text-attachment-file">
                                                          File Size:{" "}
                                                          {attachment.file_size ||
                                                            "NA"}{" "}
                                                          KB
                                                        </div>
                                                      </div>
                                                      <div className="file-footer">
                                                        <a
                                                          href={`${API_URL}/api/download_attachment/${attachment.file_id}/${attachment.file_type}`}
                                                          download
                                                          className="file-download-attachment file-txt"
                                                        >
                                                          Download
                                                        </a>
                                                        <a
                                                          href={`${API_URL}/api/view_attachment/${attachment.file_id}/${attachment.file_type}`}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="file-download-attachment file-txt"
                                                        >
                                                          View
                                                        </a>
                                                      </div>
                                                    </div>
                                                  </div>
                                                )
                                              )}
                                          </div>
                                        ) : (
                                          <div className="no-attachments">
                                            No PR Copy attachments found
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="center-alignment">
                          <span
                            className="inquiry-file-btns"
                            onClick={() => handleUserIconClick(index)}
                            ref={buttonRef}
                          >
                            <MdAttachFile className="inquiry-file-icon" />
                          </span>
                          {isPopupUserOpen === index && (
                            <div
                              className="inquiry-attach-popup User-popup"
                              ref={popupRef}
                            >
                              <div className="attach-popup-contant">
                                <div className="attachment-header">
                                  <div className="attachment-header-content">
                                    User Manual Attachments
                                  </div>
                                  <button
                                    className="fiile-close-btn"
                                    onClick={handleUserCloseAttachment}
                                  >
                                    <IoCloseOutline className="file-close-icon" />
                                  </button>
                                </div>
                                <div
                                  className="attach-popup-contant-body"
                                  key={index}
                                >
                                  <div className="inquiry-attach-file-containerssss">
                                    <div className="attach-file-showsss">
                                      <div className="text-attachmentss">
                                        {item.files &&
                                          item.files.filter(
                                            (file) =>
                                              file.file_type === "user_manual"
                                          ).length > 0 ? (
                                          <div className="attach-popup-content-body">
                                            {item.files
                                              .filter(
                                                (file) =>
                                                  file.file_type ===
                                                  "user_manual"
                                              )
                                              .map(
                                                (
                                                  attachment,
                                                  attachmentIndex
                                                ) => (
                                                  <div
                                                    className="inquiry-attach-file-container"
                                                    key={attachmentIndex}
                                                  >
                                                    <div className="inquiry-attacth-file-model">
                                                      <div className="attach-file-show">
                                                        <div className="text-attachment">
                                                          {attachment.filename
                                                            .length > 20
                                                            ? `${attachment.filename.substring(
                                                              0,
                                                              15
                                                            )}...`
                                                            : attachment.filename}
                                                        </div>
                                                        <div className="text-attachment-file">
                                                          File Size:{" "}
                                                          {attachment.file_size ||
                                                            "NA"}{" "}
                                                          KB
                                                        </div>
                                                      </div>
                                                      <div className="file-footer">
                                                        <a
                                                          href={`${API_URL}/api/download_attachment/${attachment.file_id}/${attachment.file_type}`}
                                                          download
                                                          className="file-download-attachment file-txt"
                                                        >
                                                          Download
                                                        </a>
                                                        <a
                                                          href={`${API_URL}/api/view_attachment/${attachment.file_id}/${attachment.file_type}`}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="file-download-attachment file-txt"
                                                        >
                                                          View
                                                        </a>
                                                      </div>
                                                    </div>
                                                  </div>
                                                )
                                              )}
                                          </div>
                                        ) : (
                                          <div className="no-attachments">
                                            No User Manual attachments found
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-users-container">
                  <div className="no-users-message">No Equipment Found</div>
                </div>
              )}
            </div>
            <Pagination
              showPerPage={itemsPerPage}
              total={totalItems}
              onPaginationChange={handlePaginationChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EquipmentCountView;
